import "@radix-ui/themes/styles.css"
import { UserSecurity, UserSessions, WorkOsWidgets } from "@workos-inc/widgets"
import "@workos-inc/widgets/styles.css"
import PropTypes from "prop-types"
import React, { useCallback, useState } from "react"
import { useQuery, useQueryClient } from "react-query"
import ResetPassword from "src/main/Account/Settings/ResetPassword"

import Button from "src/components/Button"
import HeadingPageTitle from "src/components/Heading/HeadingPageTitle"

import usePopupAuth from "src/hooks/use_popup_auth"

import { queryApi } from "src/utils/api/ApiMethods"

const fetchWidgetToken = () =>
  queryApi("/account/settings/login_and_security/widget_token").then(
    ({ token }) => token
  )

// staleTime de-dupes the two widgets' simultaneous fetches into one request.
// retry: false + refetchOnWindowFocus: false so a 422 goes straight to re-auth
// and a verified user isn't re-probed (and bounced) on focus.
const widgetTokenQuery = {
  queryKey: ["workosWidgetToken"],
  queryFn: fetchWidgetToken,
  staleTime: 15000,
  retry: false,
  refetchOnWindowFocus: false,
}

const WorkOsUserSecurityPanel = () => {
  const queryClient = useQueryClient()
  const [authError, setAuthError] = useState(null)

  // Probe for a usable WorkOS session before rendering the widgets. A user on a
  // legacy Devise session has no sealed cookie to mint a token from, so this
  // errors and we prompt WorkOS re-auth rather than render a broken widget.
  const { isLoading, isError, isFetching, refetch } = useQuery(widgetTokenQuery)

  const reauthenticate = usePopupAuth({
    onSuccess: () => {
      setAuthError(null)
      refetch()
    },
    onError: setAuthError,
  })

  // The widgets call authToken() themselves — on mount and ~30s before the JWT
  // expires — so we hand them an imperative getter that shares the probe's
  // cache and re-mints on demand.
  const getWidgetToken = useCallback(
    () => queryClient.fetchQuery(widgetTokenQuery),
    [queryClient]
  )

  if (isLoading) {
    return <p>Loading security settings…</p>
  }

  if (isError) {
    return (
      <div className="flex flex-col items-start space-y-4 pt-8">
        <p>
          Confirm your identity to manage your password and two-factor settings.
        </p>
        <Button
          type="button"
          variant="primary"
          isLoading={isFetching}
          onClick={() => reauthenticate(window.location.pathname)}
        >
          Verify your identity
        </Button>
        {authError && <p className="text-sm text-red-600">{authError}</p>}
      </div>
    )
  }

  return (
    <WorkOsWidgets>
      <UserSecurity authToken={getWidgetToken} />
      <div>
        <br />
        <h4>Sessions</h4>
        <UserSessions authToken={getWidgetToken} />
      </div>
    </WorkOsWidgets>
  )
}

const LoginAndSecurity = ({
  minimumPasswordLength,
  workosUserSecurityWidgetEnabled,
}) => (
  <div className="grid grid-cols-12">
    <div className="col-span-12 mb-8 mt-1">
      <HeadingPageTitle>Login and Security</HeadingPageTitle>
    </div>
    <div className="col-span-12 lg:col-span-6">
      {workosUserSecurityWidgetEnabled ? (
        <WorkOsUserSecurityPanel />
      ) : (
        <ResetPassword minimumPasswordLength={minimumPasswordLength} />
      )}
    </div>
  </div>
)

LoginAndSecurity.propTypes = {
  minimumPasswordLength: PropTypes.number.isRequired,
  workosUserSecurityWidgetEnabled: PropTypes.bool.isRequired,
}

export default LoginAndSecurity
